// Data model + pure helpers for the Photoshoots table (PX-386).
//
// Everything here is client-side: `GET /admin/photoshoots` returns the full flat
// list, and we filter / facet / explode / group it in the browser. The table
// engine (TanStack) handles grouping + expansion + column visibility; the tricky
// array-valued bits (tags/plan multi-membership, faceted counts) live here as
// plain functions so they stay testable and predictable.

import type { AdminPhotoshootResponse, AdminPlanResponse } from '@/types/api'

// A table row = a photoshoot, possibly EXPLODED for array-valued grouping.
// When grouping by `tags` and/or `plan`, one photoshoot yields one row per
// (tag × plan) membership so it appears under each of its groups. The synthetic
// `_g_*` fields carry the single grouping value for that exploded copy.
export type PhotoshootRow = AdminPhotoshootResponse & {
  _rowKey: string
  _g_tags?: string
  _g_plan?: string
  _g_model?: string
}

// The REAL models a photoshoot offers (PX-386): its variants if any, else the
// (vestigial, PX-277) default model as a single fallback. Drives the Model
// column, the model facet/filter, and model grouping — so a Seedream variant
// shows up instead of the auto-assigned default everywhere.
export interface EffModel {
  slug: string
  label: string
  cost: number
}

export function effectiveModels(ps: AdminPhotoshootResponse): EffModel[] {
  if (ps.variant_models.length) {
    return ps.variant_models.map((v) => ({
      slug: v.model_slug,
      label: v.model_display_name || v.model_name,
      cost: v.model_credit_cost,
    }))
  }
  return [{ slug: ps.model_slug, label: ps.model_name, cost: ps.model_credit_cost }]
}

export type GroupDimId = 'model' | 'category' | 'tags' | 'plan' | 'active' | 'trending'

// Group-by dimensions. `columnId` matches the TanStack column id so the
// `grouping` state is just the ordered list of these ids. `array` dims
// (tags/plan) trigger row explosion + multi-membership.
export const GROUP_DIMS: { id: GroupDimId; label: string; array: boolean }[] = [
  { id: 'model', label: 'Model', array: true },
  { id: 'category', label: 'Category', array: false },
  { id: 'tags', label: 'Tags', array: true },
  { id: 'plan', label: 'Plan', array: true },
  { id: 'active', label: 'Active', array: false },
  { id: 'trending', label: 'Trending', array: false },
]

export const GROUP_DIM_BY_ID = Object.fromEntries(GROUP_DIMS.map((d) => [d.id, d])) as Record<
  GroupDimId,
  (typeof GROUP_DIMS)[number]
>

// Sentinel grouping keys for "empty" buckets.
export const CATEGORY_NONE = '' // null/empty category → "Uncategorised"
export const TAG_NONE = '' // no tags → "No tags"
export const PLAN_ALL = '__all__' // empty allowed_plan_ids = visible to everyone

// ── Filters ────────────────────────────────────────────────────────────────

export interface TableFilters {
  search: string
  category: string[] // selected category values ('' = Uncategorised)
  tags: string[] // match-any ('' = No tags)
  model: string[] // model_slug values
  plan: number[] // plan ids
  aspect: string[] // aspect-ratio strings
  active: boolean | null // null = any
  trending: boolean | null
  kind: 'all' | 'style' | 'upscale' // PX-448: destination ('all' = any)
}

export const EMPTY_FILTERS: TableFilters = {
  search: '',
  category: [],
  tags: [],
  model: [],
  plan: [],
  aspect: [],
  active: null,
  trending: null,
  kind: 'all',
}

export function hasActiveFilters(f: TableFilters): boolean {
  return (
    f.search.trim() !== '' ||
    f.category.length > 0 ||
    f.tags.length > 0 ||
    f.model.length > 0 ||
    f.plan.length > 0 ||
    f.aspect.length > 0 ||
    f.active !== null ||
    f.trending !== null ||
    f.kind !== 'all'
  )
}

// AND across filter fields; OR (match-any) within a multi-select. A photoshoot
// with empty allowed_plan_ids is visible to everyone, so it matches ANY plan
// filter.
export function applyFilters(
  rows: AdminPhotoshootResponse[],
  f: TableFilters,
): AdminPhotoshootResponse[] {
  const q = f.search.trim().toLowerCase()
  return rows.filter((r) => {
    if (q && !(`${r.name} ${r.slug}`.toLowerCase().includes(q))) return false
    if (f.category.length && !f.category.includes(r.category ?? CATEGORY_NONE)) return false
    if (f.tags.length) {
      const rowTags = r.tags.length ? r.tags : [TAG_NONE]
      if (!rowTags.some((t) => f.tags.includes(t))) return false
    }
    if (f.model.length && !effectiveModels(r).some((m) => f.model.includes(m.slug))) return false
    if (f.plan.length) {
      const everyone = r.allowed_plan_ids.length === 0
      if (!everyone && !r.allowed_plan_ids.some((p) => f.plan.includes(p))) return false
    }
    if (f.aspect.length && !r.allowed_aspect_ratios.some((a) => f.aspect.includes(a))) return false
    if (f.active !== null && r.is_active !== f.active) return false
    if (f.trending !== null && r.is_trending_pinned !== f.trending) return false
    if (f.kind !== 'all' && r.kind !== f.kind) return false
    return true
  })
}

// ── Faceted options (counts from the full set, not cross-filtered) ───────────

export interface FacetOption {
  value: string // stringified for checkbox keys
  label: string
  count: number
}

function tally(values: string[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const v of values) m.set(v, (m.get(v) ?? 0) + 1)
  return m
}

export interface Facets {
  category: FacetOption[]
  tags: FacetOption[]
  model: FacetOption[]
  plan: FacetOption[]
  aspect: FacetOption[]
}

export function computeFacets(
  rows: AdminPhotoshootResponse[],
  plans: AdminPlanResponse[],
): Facets {
  const planName = new Map(plans.map((p) => [p.id, p.name]))
  const modelName = new Map(rows.flatMap((r) => effectiveModels(r).map((m) => [m.slug, m.label] as const)))

  const catCounts = tally(rows.map((r) => r.category ?? CATEGORY_NONE))
  const tagCounts = tally(rows.flatMap((r) => (r.tags.length ? r.tags : [TAG_NONE])))
  const modelCounts = tally(rows.flatMap((r) => effectiveModels(r).map((m) => m.slug)))
  const planCounts = tally(rows.flatMap((r) => (r.allowed_plan_ids.length ? r.allowed_plan_ids.map(String) : [PLAN_ALL])))
  const aspectCounts = tally(rows.flatMap((r) => r.allowed_aspect_ratios))

  const sortByLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label)

  return {
    category: [...catCounts].map(([value, count]) => ({
      value,
      label: value === CATEGORY_NONE ? 'Uncategorised' : value,
      count,
    })).sort(sortByLabel),
    tags: [...tagCounts].map(([value, count]) => ({
      value,
      label: value === TAG_NONE ? 'No tags' : value,
      count,
    })).sort(sortByLabel),
    model: [...modelCounts].map(([value, count]) => ({
      value,
      label: modelName.get(value) ?? value,
      count,
    })).sort(sortByLabel),
    plan: [...planCounts].map(([value, count]) => ({
      value,
      label: value === PLAN_ALL ? 'All plans (everyone)' : planName.get(Number(value)) ?? `#${value}`,
      count,
    })).sort(sortByLabel),
    aspect: [...aspectCounts].map(([value, count]) => ({ value, label: value, count })).sort(sortByLabel),
  }
}

// ── Explode for array-valued grouping ────────────────────────────────────────

// Duplicate each photoshoot into one row per (tag × plan) membership IFF those
// dims are currently grouped. Non-array dims (category/model/booleans) never
// duplicate. Empty arrays collapse to a sentinel bucket so the style still
// appears under "No tags" / "All plans".
export function explode(rows: AdminPhotoshootResponse[], grouping: string[]): PhotoshootRow[] {
  const byTag = grouping.includes('tags')
  const byPlan = grouping.includes('plan')
  const byModel = grouping.includes('model')
  if (!byTag && !byPlan && !byModel) {
    return rows.map((r) => ({ ...r, _rowKey: r.id }))
  }
  const out: PhotoshootRow[] = []
  for (const r of rows) {
    let variants: { _g_tags?: string; _g_plan?: string; _g_model?: string }[] = [{}]
    if (byTag) {
      const tags = r.tags.length ? r.tags : [TAG_NONE]
      variants = variants.flatMap((v) => tags.map((t) => ({ ...v, _g_tags: t })))
    }
    if (byPlan) {
      const planKeys = r.allowed_plan_ids.length ? r.allowed_plan_ids.map(String) : [PLAN_ALL]
      variants = variants.flatMap((v) => planKeys.map((p) => ({ ...v, _g_plan: p })))
    }
    if (byModel) {
      const modelSlugs = effectiveModels(r).map((m) => m.slug) // always ≥ 1 (fallback to default)
      variants = variants.flatMap((v) => modelSlugs.map((s) => ({ ...v, _g_model: s })))
    }
    for (const v of variants) {
      out.push({
        ...r,
        ...v,
        _rowKey: `${r.id}|${v._g_tags ?? ''}|${v._g_plan ?? ''}|${v._g_model ?? ''}`,
      })
    }
  }
  return out
}

// ── Group label rendering ────────────────────────────────────────────────────

export interface GroupLabelCtx {
  planName: Map<number, string>
  modelName: Map<string, string>
}

export function formatGroupValue(columnId: string, key: unknown, ctx: GroupLabelCtx): string {
  const s = String(key ?? '')
  switch (columnId) {
    case 'category':
      return s === CATEGORY_NONE ? 'Uncategorised' : s
    case 'tags':
      return s === TAG_NONE ? 'No tags' : s
    case 'model':
      return ctx.modelName.get(s) ?? s
    case 'plan':
      return s === PLAN_ALL ? 'All plans (everyone)' : ctx.planName.get(Number(s)) ?? `#${s}`
    case 'active':
      return s === 'true' ? 'Active' : 'Inactive'
    case 'trending':
      return s === 'true' ? 'Trending' : 'Not trending'
    default:
      return s
  }
}
